import { Fragment, useMemo, type ComponentType, type ReactNode } from 'react'
import { useIntl, type IntlShape } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import noop from 'lib/function/noop'
import { escapeRegex } from 'lib/string/regexUtils'
import ChatLoadingIndicator from './ChatLoadingIndicator'

import type { MessagePart } from './Message'
import Message from './Message'
import FailedMessageBanner from './FailedMessageBanner'
import type { FlightSearchDrawerComponentProps } from './ChatFlightSearchContent'
import type { ChatCartComponentProps } from './chatCartTypes'
import type { ChatAttachmentSummary } from 'tripPlanner/api/types'
import { stripContextTags } from 'tripPlanner/utils/contextTag'
import { userMessagePrefersPlainAssistantUi } from 'tripPlanner/utils/plainAskAIAssistantUi'
import AssistantResponseToolbar from './AssistantResponseToolbar'
import { StaffMessageRewindControls, StaffRewindBanner } from './StaffRewindControls'
import AddedItineraryItemLink from './AddedItineraryItemLink'
import { useStaffRewind } from 'tripPlanner/assistant/contexts/StaffRewindContext'
import { filterMessagesUpToStaffRewindPoint } from 'tripPlanner/utils/staffRewindMessages'
import { MESSAGE_ROLES, PART_TYPES, UI_COMPONENT_TYPES } from './constants'
import BodyText from 'LuxKit/Typography/BodyText'
import type { ChatToolLoadingKey } from 'tripPlanner/config/chatToolLoading'
import { EMPTY_SUMMARIES, type ChatLoadingStatus } from 'tripPlanner/assistant/runtime/chatStream'
import { hasAssistantReplyContentStarted } from 'tripPlanner/assistant/runtime/chatStream/chatScrollGeometry'
import { INTL_DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'

/** Matches `<Group gap={24}>` below — keep in sync when changing message spacing. */
const MESSAGE_LIST_GAP_PX = 24
const LOADING_AFTER_PLACEHOLDER_GAP_PX = 12
const LOADING_AFTER_CONTENT_GAP_PX = 4
const LOADING_AFTER_PLACEHOLDER_CLASS = 'after-placeholder'

const PlaceholderMessage = styled.div`
  height: 0;
  overflow: hidden;
  /* Cancel the message-list gap for zero-height placeholders so the loader can sit closer. */
  margin-block-start: ${rem(-MESSAGE_LIST_GAP_PX)};
`

const AssistantToolbarWrapper = styled.div`
  margin-block-start: ${rem(-12)};
`

const MessagesListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const LoadingIndicatorWrapper = styled.div`
  margin-block-start: ${rem(LOADING_AFTER_CONTENT_GAP_PX)};

  &.${LOADING_AFTER_PLACEHOLDER_CLASS} {
    margin-block-start: ${rem(LOADING_AFTER_PLACEHOLDER_GAP_PX)};
  }
`

const MessageWithDateSeparator = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  width: 100%;
`

const MessageDateSeparator = styled(BodyText)`
  width: 100%;
  text-align: center;
  color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
`

export interface MessageItem {
  id: string
  parts: Array<MessagePart>
  role: 'user' | 'assistant'
  messageType?: string
  /** BE-assigned conversation sequence number -- stable identity for activity-log references, unlike a locally-recomputed position. */
  messageSeq?: number
  isMarkdown?: boolean
  files?: Array<ChatAttachmentSummary>
  rawContent?: string
  oaiConversationId?: string
  generationStatus?: string
  generationStartedAt?: string
  generationFinishedAt?: string
  createdAt?: string
  assistantTurnPolicySnapshot?: unknown
  isFinalized?: boolean
  /** v3: placeholder overlay — present in DOM for scroll calculations but renders as zero-height until content arrives */
  isPlaceholder?: boolean
}

interface MessagesListProps {
  messages: Array<MessageItem>
  chatId?: number
  isLoading?: boolean
  isUpdatingItinerary?: boolean
  /** Toolbar callbacks — consumed by AssistantResponseToolbar */
  onRegenerate?: () => void
  onCopy?: () => void
  disableRegenerateButton?: boolean
  disableCopyButton?: boolean
  showAssistantActions?: boolean
  showRegenerateButton?: boolean
  failedMessageId?: string
  hasRetriedFailedMessage?: boolean
  onRetryFailedMessage?: () => void
  onContinueFailedMessageInNewChat?: () => void
  isStreaming?: boolean
  copiedRecently?: boolean
  onThumbsUp?: () => void
  onThumbsDown?: () => void
  feedbackSubmitted?: 'UP' | 'DOWN'
  /**
   * Live multi-choice selection from the page.
   * Applied to the last message only; older messages use their
   * historical selection computed from message content.
   */
  externalSelection?: Array<string>
  activeToolLoadingKey?: ChatToolLoadingKey
  /** Device network status via navigator.onLine. */
  isOnline?: boolean
  /** Cycling summary messages from Groq for the reasoning phase. */
  reasoningSummaries?: ReadonlyArray<string>
  /** Current tool call info for the tool_calling phase. */
  toolCallInfo?: { tool: string; args: Record<string, unknown> }
  /** Current loading status for phase-specific fallback labels. */
  loadingStatus?: ChatLoadingStatus
  /** Enables Trip Planner-only message actions such as add-to-trip controls. */
  showTripPlannerActions?: boolean
  /** Opens offer carousel cards as new-tab links instead of Trip Chat drawers. */
  openOfferLinksInNewTab?: boolean
  FlightSearchDrawerComponent?: ComponentType<FlightSearchDrawerComponentProps>
  CartComponent?: ComponentType<ChatCartComponentProps>
  /** Applies private-chat treatment to user messages. */
  isPrivateChat?: boolean
  /**
   * Shows the "See updated itinerary" link on the turn that added to the trip. Only the
   * mobile surface needs it: desktop keeps the Timeline visible alongside the chat, so
   * there is nothing to navigate to.
   */
  showAddedItineraryItemLink?: boolean
  /** Optional Trip Chat content anchored after the third finalized assistant response. */
  thirdAssistantResponseSlot?: ReactNode
}

interface MessageMetadata {
  previousRole: 'user' | 'assistant' | undefined
  isLastMessage: boolean
  multiChoiceInitialSelection: Array<string> | undefined
  previousChoiceOptions: Array<string> | undefined
  confirmationPromptActionSelection: 'confirm' | 'cancel' | undefined
  dateSeparatorLabel: string | undefined
  plainAssistantUi: boolean
}

function getConfirmationPromptPendingActionId(parts: Array<MessagePart>): string | undefined {
  for (const part of parts) {
    if (
      part.type === PART_TYPES.UI_COMPONENT &&
      part.data.type === UI_COMPONENT_TYPES.CONFIRMATION_PROMPT
    ) {
      return part.data.pendingActionId
    }
  }

  return undefined
}

function parseConfirmationPromptActionSelection(
  userContent: string,
  pendingActionId: string | undefined,
): 'confirm' | 'cancel' | undefined {
  if (!pendingActionId) {
    return undefined
  }

  const visibleContent = stripContextTags(userContent).trim()
  // Exact typed replies should collapse the matching prompt in the same way
  // as clicking the built-in button.

  if (
    userContent.includes(`Confirm staged action pending_action_id=${pendingActionId}`)
    || visibleContent === 'Confirm'
  ) {
    return 'confirm'
  }

  if (
    userContent.includes(`Cancel staged action pending_action_id=${pendingActionId}`)
    || visibleContent === 'Cancel'
  ) {
    return 'cancel'
  }

  return undefined
}

function getChoiceOptions(parts: Array<MessagePart>): Array<string> {
  const allOptions: Array<string> = []
  for (const part of parts) {
    if (
      part.type === PART_TYPES.UI_COMPONENT &&
      (part.data.type === UI_COMPONENT_TYPES.PILLS_LIST ||
        part.data.type === UI_COMPONENT_TYPES.CARDS_LIST)
    ) {
      const options = part.data.options.map((opt: { label: string } | { title: string }) => {
        return 'label' in opt ? opt.label : opt.title
      })
      allOptions.push(...options)
    }
  }
  return allOptions
}

function parseSelectedLabels(userContent: string, options: Array<string>): Array<string> {
  return options.filter(label => {
    const pattern = new RegExp(`(^|\\s|,|\\n)${escapeRegex(label)}(\\s|,|\\n|$)`, 'i')
    return pattern.test(userContent)
  })
}

function getMessageDateKey(createdAt?: string): string | undefined {
  if (!createdAt) return undefined

  const date = new Date(createdAt)
  if (Number.isNaN(date.getTime())) return undefined

  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`
}

function formatMessageDateLabel(createdAt: string | undefined, intl: IntlShape): string | undefined {
  if (!createdAt) return undefined

  const date = new Date(createdAt)
  if (Number.isNaN(date.getTime())) return undefined

  return intlDateFormatter(date, INTL_DMY_CASUAL_SHORT_FORMAT, intl.locale)
}

function isLoaderOnlyAssistantMessage(message: MessageItem, isLoading: boolean): boolean {
  if (message.role !== MESSAGE_ROLES.ASSISTANT) return false
  if (message.isPlaceholder) return true
  return isLoading && !hasAssistantReplyContentStarted(message)
}

function shouldHideDateSeparators(messages: Array<MessageItem>): boolean {
  const datedMessages = messages.filter(message => getMessageDateKey(message.createdAt) !== undefined)
  if (datedMessages.length === 0) return false

  const todayDateKey = getMessageDateKey(new Date().toISOString())
  if (!todayDateKey) return false

  return datedMessages.every(message => getMessageDateKey(message.createdAt) === todayDateKey)
}

function getMessageTextContent(message: MessageItem | undefined): string | undefined {
  if (!message) return undefined

  if (message.rawContent) return message.rawContent

  return message.parts
    .filter((part): part is Extract<MessagePart, { type: 'text' }> => part.type === PART_TYPES.TEXT)
    .map(textPart => textPart.content)
    .join('')
}

function buildMessageMetadata(messages: Array<MessageItem>, intl: IntlShape): Array<MessageMetadata> {
  const hideDateSeparators = shouldHideDateSeparators(messages)

  return messages.map((message, messageIndex) => {
    const previousMessage = messageIndex > 0 ? messages[messageIndex - 1] : undefined
    const previousRole = previousMessage?.role
    const isLastMessage = messageIndex === messages.length - 1
    const currentDateKey = getMessageDateKey(message.createdAt)
    const previousDateKey = getMessageDateKey(previousMessage?.createdAt)
    const dateSeparatorLabel = !hideDateSeparators && currentDateKey && currentDateKey !== previousDateKey
      ? formatMessageDateLabel(message.createdAt, intl)
      : undefined
    let multiChoiceInitialSelection: Array<string> | undefined
    let confirmationPromptActionSelection: 'confirm' | 'cancel' | undefined

    if (message.role === MESSAGE_ROLES.ASSISTANT && !isLastMessage) {
      const nextMessage = messages[messageIndex + 1]
      if (nextMessage?.role === 'user') {
        const userContent = getMessageTextContent(nextMessage) ?? ''
        const options = getChoiceOptions(message.parts)
        if (options.length > 0) {
          multiChoiceInitialSelection = parseSelectedLabels(stripContextTags(userContent), options)
        }
        confirmationPromptActionSelection = parseConfirmationPromptActionSelection(
          userContent,
          getConfirmationPromptPendingActionId(message.parts),
        )
      }
    }

    const previousChoiceOptions =
      message.role === MESSAGE_ROLES.USER && previousRole === MESSAGE_ROLES.ASSISTANT
        ? getChoiceOptions(previousMessage?.parts ?? [])
        : undefined

    const previousUserContent = previousRole === MESSAGE_ROLES.USER
      ? getMessageTextContent(previousMessage)
      : undefined
    return {
      previousRole,
      isLastMessage,
      multiChoiceInitialSelection,
      previousChoiceOptions,
      confirmationPromptActionSelection,
      dateSeparatorLabel,
      plainAssistantUi: message.role === MESSAGE_ROLES.ASSISTANT
        && userMessagePrefersPlainAssistantUi(previousUserContent),
    }
  })
}

function MessagesList({
  messages,
  chatId,
  isLoading = false,
  isUpdatingItinerary = false,
  onRegenerate = noop,
  onCopy = noop,
  disableRegenerateButton = false,
  disableCopyButton = false,
  showAssistantActions = false,
  showRegenerateButton = true,
  failedMessageId,
  hasRetriedFailedMessage = false,
  onRetryFailedMessage,
  onContinueFailedMessageInNewChat,
  isStreaming = false,
  copiedRecently = false,
  onThumbsUp = noop,
  onThumbsDown = noop,
  feedbackSubmitted,
  externalSelection,
  activeToolLoadingKey,
  isOnline = true,
  reasoningSummaries = EMPTY_SUMMARIES,
  toolCallInfo,
  loadingStatus = 'idle',
  showTripPlannerActions = true,
  openOfferLinksInNewTab = false,
  FlightSearchDrawerComponent,
  CartComponent,
  isPrivateChat = false,
  showAddedItineraryItemLink = false,
  thirdAssistantResponseSlot,
}: MessagesListProps) {
  const intl = useIntl()
  const { inspectedMessageId, isInspecting } = useStaffRewind()
  const displayMessages = useMemo(
    () => filterMessagesUpToStaffRewindPoint(messages, inspectedMessageId),
    [messages, inspectedMessageId],
  )
  const isThumbsUp = feedbackSubmitted === 'UP'
  const isThumbsDown = feedbackSubmitted === 'DOWN'
  const isThumbsIconDisabled = feedbackSubmitted !== undefined
  const messageMetadata = useMemo(() => buildMessageMetadata(displayMessages, intl), [displayMessages, intl])
  const thirdFinalizedAssistantMessageId = useMemo(() => {
    return displayMessages
      .filter(message => message.role === MESSAGE_ROLES.ASSISTANT && message.isFinalized)
      .at(2)?.id
  }, [displayMessages])

  const lastMessage = displayMessages.at(-1)
  const shouldShowAssistantToolbar = showAssistantActions && !isInspecting
  const shouldRenderThirdAssistantResponseSlotAfterToolbar = shouldShowAssistantToolbar
    && lastMessage?.id === thirdFinalizedAssistantMessageId
  const trailingIsLoaderOnly = Boolean(
    lastMessage && isLoaderOnlyAssistantMessage(lastMessage, isLoading),
  )
  const loadingIndicatorClassName = trailingIsLoaderOnly
    ? LOADING_AFTER_PLACEHOLDER_CLASS
    : undefined

  return (
    <MessagesListWrapper>
      <StaffRewindBanner />
      <Group direction="vertical" gap={MESSAGE_LIST_GAP_PX} fullWidth>
        {
          displayMessages.map((message, messageIndex) => {
            const {
              isLastMessage,
              multiChoiceInitialSelection,
              previousChoiceOptions,
              confirmationPromptActionSelection,
              dateSeparatorLabel,
              plainAssistantUi,
            } = messageMetadata[messageIndex]
            const renderAsPlaceholder = message.isPlaceholder || (
              !isInspecting
              && message.id === lastMessage?.id
              && isLoaderOnlyAssistantMessage(message, isLoading)
            )

            if (renderAsPlaceholder) {
              return <PlaceholderMessage key={message.id} data-message-id={message.id} />
            }

            return (
              <Fragment key={message.id}>
                <MessageWithDateSeparator>
                  {dateSeparatorLabel && (
                    <MessageDateSeparator variant="small">
                      {dateSeparatorLabel}
                    </MessageDateSeparator>
                  )}
                  <Message
                    messageId={message.id}
                    parts={message.parts}
                    role={message.role}
                    isMarkdown={message.isMarkdown}
                    files={message.files}
                    rawContent={message.rawContent}
                    oaiConversationId={message.oaiConversationId}
                    generationStatus={message.generationStatus}
                    generationStartedAt={message.generationStartedAt}
                    generationFinishedAt={message.generationFinishedAt}
                    chatId={chatId}
                    isChatStreaming={isStreaming}
                    isFinalized={message.isFinalized}
                    isLastMessage={isLastMessage}
                    multiChoiceInitialSelection={multiChoiceInitialSelection}
                    externalSelection={externalSelection}
                    previousChoiceOptions={previousChoiceOptions}
                    confirmationPromptActionSelection={confirmationPromptActionSelection}
                    showTripPlannerActions={showTripPlannerActions}
                    openOfferLinksInNewTab={openOfferLinksInNewTab}
                    FlightSearchDrawerComponent={FlightSearchDrawerComponent}
                    CartComponent={CartComponent}
                    isPrivateChat={isPrivateChat}
                    plainAssistantUi={plainAssistantUi}
                  />
                  {message.role === MESSAGE_ROLES.ASSISTANT && message.generationStatus === 'FAILED' && onRetryFailedMessage && onContinueFailedMessageInNewChat && (
                    <FailedMessageBanner
                      messageId={message.id}
                      active={message.id === failedMessageId && !isInspecting}
                      hasRetried={hasRetriedFailedMessage}
                      isStreaming={isStreaming}
                      onRetry={onRetryFailedMessage}
                      onContinueInNewChat={onContinueFailedMessageInNewChat}
                    />
                  )}
                  {showAddedItineraryItemLink && message.role === MESSAGE_ROLES.ASSISTANT && (
                    <AddedItineraryItemLink messageId={message.id} />
                  )}
                  {message.role === MESSAGE_ROLES.ASSISTANT && message.isFinalized && (
                    <StaffMessageRewindControls messageId={message.id} />
                  )}
                </MessageWithDateSeparator>
                {thirdAssistantResponseSlot
                  && !shouldRenderThirdAssistantResponseSlotAfterToolbar
                  && message.id === thirdFinalizedAssistantMessageId
                  && thirdAssistantResponseSlot}
              </Fragment>
            )
          })
        }
        {shouldShowAssistantToolbar &&
          <AssistantToolbarWrapper>
            <AssistantResponseToolbar
              onCopy={onCopy}
              disableCopyButton={disableCopyButton}
              copiedRecently={copiedRecently}
              showRegenerateButton={showRegenerateButton}
              onRegenerate={onRegenerate}
              disableRegenerateButton={disableRegenerateButton}
              onThumbsUp={onThumbsUp}
              onThumbsDown={onThumbsDown}
              isThumbsIconDisabled={isThumbsIconDisabled}
              isThumbsUp={isThumbsUp}
              isThumbsDown={isThumbsDown}
            />
          </AssistantToolbarWrapper>}
        {shouldRenderThirdAssistantResponseSlotAfterToolbar && thirdAssistantResponseSlot}
      </Group>
      {isLoading && !isInspecting && (
        <LoadingIndicatorWrapper
          className={loadingIndicatorClassName}
          data-testid="chat-loading-indicator"
        >
          <ChatLoadingIndicator
            isUpdatingItinerary={isUpdatingItinerary}
            activeToolLoadingKey={activeToolLoadingKey}
            isOnline={isOnline}
            reasoningSummaries={reasoningSummaries}
            toolCallInfo={toolCallInfo}
            loadingStatus={loadingStatus}
          />
        </LoadingIndicatorWrapper>
      )}
    </MessagesListWrapper>
  )
}

export default MessagesList
